import emit from '../../common/events';
import store from '../../store/store';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { BaseComponent } from '../base-component/base-component';
import { CANCELATION_FREE_TRIAL_HELP_URL, getOrderHistoryPageURL } from './utils/free-trial.util';
import { REDIRECT_URL_HECLR_KEY } from '../../common/constants/api-constants';
import { StoreState } from '../../store/store.interface';
import { TemplateResult, html } from 'lit';
import { booleanConverter } from '../../common/converters';
import { customElement, property } from 'lit/decorators.js';
import { getLoginURLWithRedirectUrlToCurrentPage } from '../../common/utils/login-utils';
import { professionalPrograms } from '../firstaid-forward/professional-program-identifier';

@customElement('mhecomm-signedout-availability-free-trial')
export class SignedoutAvailabilityFreeTrial extends BaseComponent {
  /**
   * The free trial duration in days.
   */
  @property({ type: Number })
  freeTrialDuration = 0;

  /**
   * The program id.
   */
  @property()
  programId = '';

  /**
   * Indicates if the component is being used in the cart list.
   */
  @property({ converter: booleanConverter })
  isCartList = false;

  /**
   * The ISBN of the product.
   */
  @property()
  isbn?: string;

  renderComp(): TemplateResult {
    return html`
      <div class="alert alert-success mb-0">
        <div class="fw-bold">
          ${this.isCartList
            ? html`Proceed to checkout`
            : html`<a href=${this.getLoginUrlWithRedirect(this.isbn)} @click=${() => this.fireAnalytics('Log in')}
                >Log in</a
              >`}
          to view eligibility for ${this.freeTrialDuration}-Day free trial access.
        </div>
        <p class="fs-small mb-0">
          ${this.isCartList
            ? html` <mhecomm-free-trial-remove-from-cart isbn="${this.isbn}"></mhecomm-free-trial-remove-from-cart>`
            : ''}
          Free trials are available for U.S.-based customers who have not previously had a free trial of
          ${this.getProductName()}. Free cancellation any time before the end of the trial period. Trials automatically
          continue as paid subscriptions, unless canceled before end of the respective trial period. Cancel before your
          free trial ends in
          <a
            href=${getOrderHistoryPageURL(store.getState() as StoreState)}
            @click=${() => this.fireAnalytics('My Account Order History')}
            >My Account &gt; Order History</a
          >
          to avoid getting billed.
          <a
            href=${CANCELATION_FREE_TRIAL_HELP_URL}
            aria-label="${ariaLabelWithNewTab('How do I cancel a free trial?')}"
            target="_blank"
            @click=${() => this.fireAnalytics('How do I cancel a free trial')}
            >How do I cancel a free trial?</a
          >
        </p>
      </div>
    `;
  }

  getProductName() {
    return professionalPrograms[this.programId];
  }

  getLoginUrlWithRedirect(isbn: string | undefined) {
    const redirectUrl = new URL(getLoginURLWithRedirectUrlToCurrentPage());
    if (isbn) {
      const redirect = new URL(redirectUrl.searchParams.get(REDIRECT_URL_HECLR_KEY)!);
      redirect.searchParams.set('isbn', isbn);

      redirectUrl.searchParams.set(REDIRECT_URL_HECLR_KEY, redirect.toString());
      return redirectUrl.toString();
    }

    return redirectUrl.toString();
  }

  private fireAnalytics(eventName: string) {
    emit({
      element: this,
      name: 'click',
      detail: {
        label: eventName,
        eventName: eventName.toLowerCase().replace(/\s/g, '-'),
        verbiage: 'signedout-availability-free-trial',
      },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-signedout-availability-free-trial': SignedoutAvailabilityFreeTrial;
  }
}
